import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, formatDateTimeBR } from '../api'
import type { SyncStatus, User } from '../api'

// Backup e sincronização: só o que o operador precisa saber — o que está
// pendente, quando foi o último envio e um botão para forçar.
export function Backup({ user }: { user: User }) {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setStatus(await api.sync.status())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao consultar o estado')
    }
  }, [])

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), 10_000)
    return () => clearInterval(t)
  }, [load])

  async function syncNow() {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await api.sync.now()
      setNotice('Sincronizando…')
      setTimeout(() => void load(), 2500)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao sincronizar')
    } finally {
      setBusy(false)
    }
  }

  async function requeue() {
    if (!confirm('Reenviar tudo para a nuvem?')) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const out = await api.sync.requeue()
      setNotice(`${out.requeued} registros na fila.`)
      setTimeout(() => void load(), 3000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao reenviar')
    } finally {
      setBusy(false)
    }
  }

  const pending = (status?.pendingChanges ?? 0) + (status?.pendingMedia ?? 0)
  const hasHub = status?.hub === true
  const isAdmin = user.role === 'admin'
  const emDia = hasHub && pending === 0

  return (
    <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', minHeight: 0, padding: '18px 20px', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ font: '600 17px/1.2 var(--font-sans)' }}>Backup</div>
        <span className="badge" style={{
          background: emDia ? 'var(--ok-soft)' : pending > 0 ? 'var(--accent-soft)' : 'var(--surface-2)',
          color: emDia ? 'var(--ok)' : pending > 0 ? 'var(--accent)' : 'var(--text-2)',
        }}>
          {emDia ? 'EM DIA' : pending > 0 ? `${pending} PENDENTES` : 'AGUARDANDO'}
        </span>
        <div style={{ flex: 1 }} />
        {isAdmin && hasHub && (
          <>
            <button className="btn" onClick={() => void requeue()} disabled={busy} style={{ height: 32, padding: '0 12px', color: 'var(--text-2)' }}>
              Reenviar tudo
            </button>
            <button className="btn btn-primary" onClick={() => void syncNow()} disabled={busy} style={{ height: 32, padding: '0 16px' }}>
              {busy ? 'Sincronizando…' : 'Sincronizar agora'}
            </button>
          </>
        )}
      </div>

      {error && <div style={{ marginTop: 12, padding: '9px 12px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>}
      {notice && <div style={{ marginTop: 12, padding: '9px 12px', border: '1px solid var(--ok)', borderRadius: 6, background: 'var(--ok-soft)', fontSize: 12.5 }}>{notice}</div>}

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: 900 }}>
        <Card label="Registros na fila" value={String(status?.pendingChanges ?? 0)} />
        <Card label="Imagens na fila" value={String(status?.pendingMedia ?? 0)} />
        <Card
          label="Último envio"
          value={status?.['sync.lastRun'] ? formatDateTimeBR(String(status['sync.lastRun'])) : '—'}
          note={status ? `${status['sync.lastPushed'] ?? 0} enviados · ${status['sync.lastPulled'] ?? 0} recebidos` : ''}
        />
      </div>

      {status?.['sync.lastError'] ? (
        <div style={{ marginTop: 12, maxWidth: 900, padding: '10px 13px', border: '1px solid var(--accent-line)', borderRadius: 7, background: 'var(--accent-soft)' }}>
          <div style={{ font: '600 12px/1 var(--font-sans)', color: 'var(--accent)' }}>Última tentativa não concluiu</div>
          <div style={{ marginTop: 5, font: '400 11.5px/1.6 var(--font-mono)', color: 'var(--text-2)', wordBreak: 'break-all' }}>
            {String(status['sync.lastError'])}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Card({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="card" style={{ padding: '12px 14px', background: 'var(--surface)' }}>
      <div style={{ font: '500 9.5px/1 var(--font-sans)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
      <div style={{ marginTop: 6, font: '600 17px/1.2 var(--font-sans)' }}>{value}</div>
      {note && <div style={{ marginTop: 4, font: '400 10.5px/1.4 var(--font-mono)', color: 'var(--text-2)' }}>{note}</div>}
    </div>
  )
}
